import clsx from 'clsx'
import {useFormik} from 'formik'
import {find, isEmpty, uniqBy} from 'lodash'
import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import * as Yup from 'yup'
import {ConfirmActionModal, ShowAddress} from '../../../../../_gori/components'
import {KTSVG} from '../../../../../_gori/helpers'
import useCancelToken from '../../../../../_gori/hooks/UseCancelToken'
import UseCancelTokenHasKey from '../../../../../_gori/hooks/UseCancelTokenHasKey'
import UseYupValidate from '../../../../../_gori/hooks/UseYupValidate'
import {
  InputTextFormik,
  SelectFormik,
  ValidationErrorModal,
} from '../../../../../_gori/partials/widgets'
import HttpStatusCode from '../../../../api/HttpStatusCode'
import {A1ShipFromOrderModal, ModalTemplate, SmartA1Batch} from '../../../a1'
import {useAuth} from '../../../auth'
import {FileErrorModal} from '../../../batches'
import A1_CONSTANTS from '../../core/_const'
import A1BatchService from '../../core/_requests'
import {A1BatchDuplicateModal} from './A1BatchDuplicateModal'
import {SmartA1BatchDuplicateModal} from '../smart-a1-batch'
import {ROUTES} from '../../../../../_gori/constants'
import {useNavigate} from 'react-router'

type Props = {
  show: boolean
  handleClose: () => void
  orders?: any
  file?: any
  reloadTable: (highlightBatchIds?: string[]) => any
  fromOrderPage?: boolean
}

type OrderShortInfo = {
  id: string | number
  a1_batch_id?: string | number | null
  from: {
    city?: string
    state?: string
    country?: string
    zip?: string
    company?: string
    name?: string
    phone?: string
    email?: string
    street1?: string
    street2?: string
  }
  fromString: string
  status: string
  fromOrderPage?: boolean
}

const AddToA1BatchModal: React.FC<Props> = ({
  show,
  handleClose,
  orders,
  file = null,
  reloadTable,
  fromOrderPage = false,
}) => {
  const intl = useIntl()
  const {loadingSwitch, currentUser} = useAuth()
  const {stringYup} = UseYupValidate()
  const {newCancelToken, isCancel} = useCancelToken()
  const navigate = useNavigate()
  const {newCancelTokenHasKey, isCancelHasKey} = UseCancelTokenHasKey()
  const [loading, setLoading] = useState<{first: boolean; submit: boolean}>({
    first: true,
    submit: false,
  })
  const [hasShow, setHasShow] = useState<{
    smart: boolean
    create: boolean
    existing: boolean
    batchesDuplicate: boolean
    fromAddressOrders: boolean
    confirmAction: boolean
    validateFile: boolean
    smartA1BatchDuplicate: boolean
  }>({
    smart: false,
    create: false,
    existing: false,
    batchesDuplicate: false,
    fromAddressOrders: false,
    confirmAction: false,
    validateFile: false,
    smartA1BatchDuplicate: false,
  })
  const [dataModal, setDataModal] = useState<{
    batchesDuplicate: any
    fromAddressOrders: any
    confirmAction: string
    validateFile: any
    smartA1BatchDuplicate: any
    smart: {
      orderIds?: string[]
    }
  }>({
    batchesDuplicate: [],
    fromAddressOrders: [],
    confirmAction: '',
    validateFile: [],
    smartA1BatchDuplicate: [],
    smart: {
      orderIds: [],
    },
  })
  const [hasPass, setHasPass] = useState<{shipFrom: boolean}>({shipFrom: false})
  const [validationErrors, setValidationErrors] = useState<any>()
  const [optionsBatches, setOptionsBatches] = useState<any>([])

  const shipFromToString = useCallback(
    (from: Record<string, any>) =>
      Object.keys(from)
        .sort()
        .map((k) => {
          const v = from[k]
          return typeof v === 'string' ? v.trim() : String(v || '')
        })
        .join('|'),
    []
  )

  const ordersShortInfo: OrderShortInfo[] = useMemo(() => {
    return (
      orders?.map((item) => {
        const from = {
          company: item.original.from_company || '',
          first_name: item.original.from_first_name || '',
          last_name: item.original.from_last_name || '',
          street_1: item.original.from_street1 || '',
          street_2: item.original.from_street2 || '',
          city: item.original.from_city || '',
          state: item.original.from_state || '',
          country: item.original.from_country || '',
          zip: item.original.from_zip || '',
          phone: item.original.from_phone || '',
          email: item.original.from_email || '',
        }

        return {
          id: item.original.id,
          a1_batch_id: item.original.a1_batch_id,
          from,
          fromString: shipFromToString(from),
          status: item.original.status,
        }
      }) || []
    )
  }, [orders, shipFromToString])

  const initValidateSchema = useMemo(() => {
    return {
      active_box: Yup.string()
        .nullable()
        .oneOf(
          [...Object.values(A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX), 'null'],
          intl.formatMessage({id: 'INVALID_OPTION'})
        )
        .required(intl.formatMessage({id: 'PLEASE_SELECT_AT_LEAST_ONE_OPTION'})),
      create: Yup.string()
        .nullable()
        .when('active_box', {
          is: A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE,
          then: stringYup(255, 'A1_BATCH_NAME'),
        }),
      existing: Yup.string()
        .nullable()
        .when('active_box', {
          is: A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING,
          then: stringYup(255, 'SELECT_A_A1_BATCH'),
        }),
    }
  }, [intl, stringYup])

  const goToA1BatchDetail = (batchId?: number | string) => {
    if (!batchId || !fromOrderPage) return

    navigate(`${ROUTES.A1.BASE}/${batchId}`)
  }

  const handleFileUpload = async (values) => {
    if (!file) return

    const isCreateBox = values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE
    const isExistingBox =
      values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING
    const isSmart = values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.SMART

    if (isSmart) {
      setHasShow((prev) => ({...prev, smart: true}))

      return
    }

    const createPayload = () => {
      const payload = new FormData()
      payload.append('file', file)
      payload.append('type', 'create')

      if (isCreateBox) {
        payload.append('name', values.create || '')
      } else if (isExistingBox) {
        payload.append('existing_batch', values.existing || '')
      }

      return payload
    }
    const payload = createPayload()

    const showConfirmModal = (messageKey: string) => {
      setDataModal((prev) => ({
        ...prev,
        confirmAction: messageKey,
      }))
      setHasShow((prev) => ({...prev, confirmAction: true}))
    }

    if (!hasPass.shipFrom) {
      if (isCreateBox) {
        const from = await checkUniqueAddressesInBatch(payload)
        if (!isEmpty(from) && from?.length !== 1) {
          return showConfirmModal('THE_1ST_RECORD_WILL_OVERWRITE')
        }
      } else if (isExistingBox) {
        const from = await checkOrdersInBatch(values.existing)
        return showConfirmModal(
          isEmpty(from)
            ? 'THE_1ST_RECORD_WILL_OVERWRITE'
            : 'THE_FROM_ADDRESS_OF_SELECTED_A1_BATCH_WILL_OVERWRITE'
        )
      }
    }

    const config = {
      headers: {'content-type': 'multipart/form-data'},
      cancelToken: newCancelToken(),
    }

    try {
      setLoading((prev) => ({...prev, submit: true}))
      const res = await A1BatchService.store(payload, config)

      if (res) {
        toast.success(intl.formatMessage({id: 'ADDED_TO_A1_BATCH_SUCCESSFULLY'}))
        reloadTable()
        goToA1BatchDetail(res.batch?.id)
        handleClose()
      }
    } catch (error: any) {
      if (isCancel(error)) return
      handleError(error)
    } finally {
      setLoading((prev) => ({...prev, submit: false}))
    }
  }

  const handleError = (error: any) => {
    if (error?.response?.status === HttpStatusCode.UNPROCESSABLE_ENTITY) {
      setDataModal((prev) => ({...prev, validateFile: error?.response?.data?.error?.fields}))
      setHasShow((prev) => ({...prev, validateFile: true}))
    } else {
      setValidationErrors(error?.response)
    }
  }

  const handleAddOrderManually = async (values) => {
    const isCreateBox = values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE
    const isExistingBox =
      values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING

    const shippedOrders = ordersShortInfo.filter((o) => o.status === 'shipped')
    const openOrders = ordersShortInfo.filter((o) => o.status === 'open')

    const uniqueShipFromOpenOrders = uniqBy(openOrders, 'fromString')
    const uniqueShipFromShippedOrders = uniqBy(shippedOrders, 'fromString')

    if (!hasPass.shipFrom) {
      if (isCreateBox) {
        const ok = addToNewBatch(openOrders, shippedOrders, values)
        if (!ok) return
      }

      if (isExistingBox) {
        const selectedBatch = optionsBatches.find((item) => item.value === values.existing)

        if (!selectedBatch.order_count) {
          const ok = addToNewBatch(openOrders, shippedOrders, values)
          if (!ok) return
        } else if (!shippedOrders.length) {
          if (
            uniqueShipFromOpenOrders.length > 1 ||
            uniqueShipFromOpenOrders.some(
              (o) => o.fromString !== shipFromToString(selectedBatch.ship_from)
            )
          ) {
            values.shipFrom = selectedBatch.from
            return showModal('confirmAction', 'CURRENT_A1_BATCH_WILL_OVERWRITE_SELECTED_ORDERS')
          }
          values.shipFrom = selectedBatch.from
          setHasPass((prev) => ({...prev, shipFrom: true}))
        } else if (uniqueShipFromShippedOrders.length > 1) {
          return setValidationErrors({
            data: {
              error: {
                message: intl.formatMessage({
                  id: 'ALL_SHIPMENTS_IN_A_BATCH_MUST_HAVE_THE_SAME_FROM_ADDRESS',
                }),
              },
            },
          })
        } else if (shippedOrders[0].fromString !== shipFromToString(selectedBatch.ship_from)) {
          return setValidationErrors({
            data: {
              error: {
                message: intl.formatMessage({
                  id: 'ALL_SHIPMENTS_IN_A_BATCH_MUST_HAVE_THE_SAME_FROM_ADDRESS',
                }),
              },
            },
          })
        }
      }
    }

    const orderChecked = orders.reduce(
      (acc: any, order: any) => {
        const key = order.original.a1_batch_id ? 'hasBatch' : 'noBatch'
        acc[key].push(order.original.id)
        return acc
      },
      {hasBatch: [], noBatch: []}
    )

    const payload = {
      type: A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE,
      has_batch: orderChecked.hasBatch,
      no_batch: orderChecked.noBatch,
      from: values.shipFrom,
      ...(isCreateBox ? {name: values.create} : {existing_batch: values.existing}),
    }

    // Handle orders with existing batches - show duplicate modal
    if (orderChecked.hasBatch.length > 0) {
      handleOrdersWithBatches(payload, values)
      return
    }

    if (isSelectSmartA1Batch) {
      showModal('smart', {
        orderIds: ordersShortInfo.map((o) => o.id),
      })
      return
    }

    await submitBatchCreation(payload)
  }

  const handleOrdersWithBatches = (payload: any, values: any) => {
    const titleMapping = {
      [A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING]: find(optionsBatches, {
        value: values.existing,
      })?.label,
      [A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE]: values.create,
    }

    if (isSelectSmartA1Batch) {
      showModal('smartA1BatchDuplicate', {payload})
    } else {
      showModal('batchesDuplicate', {
        payload,
        name_position: titleMapping[values.active_box],
      })
    }
  }

  const submitBatchCreation = async (payload: any) => {
    const config = {cancelToken: newCancelToken()}

    try {
      setLoading((prev) => ({...prev, submit: true}))
      const res = await A1BatchService.store(payload, config)

      if (res) {
        toast.success(intl.formatMessage({id: 'ADDED_TO_A1_BATCH_SUCCESSFULLY'}))
        reloadTable()
        goToA1BatchDetail(res.batch?.id)
        handleClose()
      }
    } catch (error: any) {
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    } finally {
      setLoading((prev) => ({...prev, submit: false}))
    }
  }

  const addToNewBatch = (openOrders: OrderShortInfo[], shippedOrders: OrderShortInfo[], values) => {
    const uniqueShipFromOpenOrders = uniqBy(openOrders, 'fromString')
    const uniqueShipFromShippedOrders = uniqBy(shippedOrders, 'fromString')

    if (!shippedOrders.length && openOrders.length) {
      if (uniqueShipFromOpenOrders.length > 1) {
        showModal('fromAddressOrders', {orderIds: ordersShortInfo.map((o) => o.id)})
        return false
      }
      values.shipFrom = uniqueShipFromOpenOrders[0].from
      setHasPass((prev) => ({...prev, shipFrom: true}))
      return true
    }

    if (shippedOrders.length && !openOrders.length) {
      if (uniqueShipFromShippedOrders.length > 1) {
        setValidationErrors({
          data: {
            error: {
              message: intl.formatMessage({
                id: 'ALL_SHIPMENTS_IN_A_BATCH_MUST_HAVE_THE_SAME_FROM_ADDRESS',
              }),
            },
          },
        })
        return false
      }
      values.shipFrom = uniqueShipFromShippedOrders[0].from
      setHasPass((prev) => ({...prev, shipFrom: true}))
      return true
    }

    if (shippedOrders.length && openOrders.length) {
      if (uniqueShipFromShippedOrders.length > 1) {
        setValidationErrors({
          data: {
            error: {
              message: intl.formatMessage({
                id: 'ALL_SHIPMENTS_IN_A_BATCH_MUST_HAVE_THE_SAME_FROM_ADDRESS',
              }),
            },
          },
        })
        return false
      }

      if (openOrders.every((o) => o.fromString === uniqueShipFromShippedOrders[0].fromString)) {
        values.shipFrom = uniqueShipFromShippedOrders[0].from
        setHasPass((prev) => ({...prev, shipFrom: true}))
        return true
      }
      values.shipFrom = uniqueShipFromShippedOrders[0].from
      showModal(
        'confirmAction',
        'ORDERS_LABEL_OVERRIDE_SHIP_FROM_ADDRESS_FOR_ORDERS_WITHOUT_LABELS'
      )
      return false
    }
    return true
  }

  const showModal = (type: string, data: any) => {
    setDataModal((prev) => ({...prev, [type]: data}))
    setHasShow((prev) => ({...prev, [type]: true}))
  }

  const checkOrdersInBatch = useCallback(
    async (batchId) => {
      setLoading((prev) => ({...prev, submit: true}))
      try {
        const {from} = await A1BatchService.getDetail(batchId, {
          cancelToken: newCancelTokenHasKey('check_orders_in_batch'),
        })
        return from
      } catch (error) {
        if (isCancelHasKey(error)) return []
        return []
      } finally {
        setLoading((prev) => ({...prev, submit: false}))
      }
    },
    [isCancelHasKey, newCancelTokenHasKey]
  )

  const checkUniqueAddressesInBatch = useCallback(
    async (payload) => {
      setLoading((prev) => ({...prev, submit: true}))
      try {
        const config = {
          headers: {'content-type': 'multipart/form-data'},
          cancelToken: newCancelTokenHasKey('check_unique_addresses_in_batch'),
        }
        const {from} = await A1BatchService.uniqueAddresses(payload, config)
        return from
      } catch (error) {
        if (isCancelHasKey(error)) return []
        return []
      } finally {
        setLoading((prev) => ({...prev, submit: false}))
      }
    },
    [isCancelHasKey, newCancelTokenHasKey]
  )

  const formik = useFormik({
    initialValues: {
      active_box: null,
      smart: null,
      create: null,
      existing: null,
      shipFrom: null,
    },
    enableReinitialize: true,
    validationSchema: Yup.object().shape(initValidateSchema),
    onSubmit: async (values) => {
      if (file) {
        await handleFileUpload(values)
      } else {
        await handleAddOrderManually(values)
      }
    },
  })

  const getBatches = useCallback(async () => {
    const config = {
      params: {
        all: true,
        latest: true,
        status: A1_CONSTANTS.A1_BATCH_STATUS.OPEN,
      },
      cancelToken: newCancelToken(),
    }
    try {
      const [{a1_batches}, {a1_batch}] = await Promise.all([
        A1BatchService.getList(config),
        A1BatchService.getA1BatchLatest({cancelToken: newCancelToken()}),
      ])

      let nameBatch = (a1_batch?.id ? a1_batch?.id + 100001 : 100001).toString()
      if (file) {
        nameBatch = `${nameBatch} ${file.name.replace(/(\.csv)+$/, '')}`
      }
      await formik.setFieldValue('create', nameBatch)
      setOptionsBatches(
        a1_batches.map((item) => ({
          label: item.name,
          value: item.id,
          order_count: item.order_count,
          ship_from: item.from,
        }))
      )
    } catch (error) {
      if (isCancel(error)) return
    } finally {
      setLoading((prev) => ({...prev, first: false}))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCancel, newCancelToken])

  useEffect(() => {
    if (loadingSwitch) return
    getBatches()

    return () => {}
  }, [getBatches, loadingSwitch])

  // BEGIN: Action Duplicate Modal
  const handleDuplicateClose = () => {
    setHasShow((prev) => ({...prev, batchesDuplicate: false}))
    handleClose()
  }

  const handleDuplicateSuccess = (batchId) => {
    reloadTable()
    goToA1BatchDetail(batchId)
    handleClose()
  }
  // END: Action Duplicate Modal

  // BEGIN: Action Ship From Modal
  const handleShipFromSubmit = (value) => {
    formik.setFieldValue('shipFrom', value)
    setHasPass((prev) => ({...prev, shipFrom: true}))
    formik.handleSubmit()
  }

  const handleShipFromClose = () => {
    setDataModal((prev) => ({...prev, fromAddressOrders: []}))
    setHasShow((prev) => ({...prev, fromAddressOrders: false}))
  }
  // END: Action Ship From Modal

  // BEGIN: Action Confirm Modal
  const contentConfirmAction = useMemo(() => {
    const selectedBatch = optionsBatches.find((item) => item.value === formik.values.existing)

    return (
      <div className='fw-bold'>
        <div className='d-flex flex-column align-items-center justify-content-center'>
          <div>
            {intl.formatMessage({id: 'ALL_SHIPMENTS_IN_A_BATCH_MUST_HAVE_THE_SAME_FROM_ADDRESS'})}
          </div>
          <div className='mt-2 text-center'>
            {dataModal?.confirmAction && intl.formatMessage({id: dataModal?.confirmAction})}
          </div>
          {dataModal?.confirmAction === 'CURRENT_A1_BATCH_WILL_OVERWRITE_SELECTED_ORDERS' && (
            <div className='mt-3 text-center'>
              <ShowAddress data={selectedBatch?.ship_from} fullnameClassName='d-block mt-2' />
            </div>
          )}
        </div>
      </div>
    )
  }, [dataModal?.confirmAction, formik.values.existing, intl, optionsBatches])

  const handleConfirmPrimaryAction = () => {
    setHasPass((prev) => ({...prev, shipFrom: true}))
    setHasShow((prev) => ({...prev, confirmAction: false}))
    formik.handleSubmit()
  }

  const handleConfirmSecondaryAction = () => {
    setHasPass((prev) => ({...prev, shipFrom: false}))
    setHasShow((prev) => ({...prev, confirmAction: false}))
  }

  const handleConfirmClose = () => {
    setHasPass((prev) => ({...prev, shipFrom: false}))
    setHasShow((prev) => ({...prev, confirmAction: false}))
  }
  // END: Action Confirm Modal

  const isSelectSmartA1Batch = useMemo(
    () => formik.values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.SMART,
    [formik.values.active_box]
  )

  const isSelectCreateNewA1Batch = useMemo(
    () => formik.values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE,
    [formik.values.active_box]
  )

  const isSelectAddToExistingA1Batch = useMemo(
    () => formik.values.active_box === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING,
    [formik.values.active_box]
  )

  const disabledSmartA1Batch = useMemo(
    () =>
      !!ordersShortInfo.find((o) => o.status === 'shipped') ||
      currentUser?.settings?.yamato_account,
    [ordersShortInfo, currentUser]
  )

  const handleSelectOption = useCallback(
    (value) => {
      if (loading.submit) return
      if (value === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.SMART) {
        if (disabledSmartA1Batch) return
      }
      formik.setFieldValue('active_box', value)
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [disabledSmartA1Batch, loading.submit]
  )

  return (
    <>
      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      {hasShow.validateFile && (
        <FileErrorModal
          show={hasShow.validateFile}
          handleClose={() => setHasShow((prev) => ({...prev, validateFile: false}))}
          errors={dataModal.validateFile}
        />
      )}
      {hasShow.smart && (
        <SmartA1Batch
          show={hasShow.smart}
          orderIds={dataModal.smart.orderIds}
          handleClose={handleClose}
          file={file}
          handleError={(errors, close = false) => {
            if (close) {
              setHasShow((prev) => ({...prev, smart: false}))
            }
            handleError(errors)
          }}
          handleCreateSuccess={(batchIds) => reloadTable(batchIds)}
        />
      )}
      {hasShow.batchesDuplicate && (
        <A1BatchDuplicateModal
          show={hasShow.batchesDuplicate}
          data={dataModal.batchesDuplicate}
          handleClose={handleDuplicateClose}
          handleSuccess={handleDuplicateSuccess}
        />
      )}
      {hasShow.smartA1BatchDuplicate && (
        <SmartA1BatchDuplicateModal
          show={hasShow.smartA1BatchDuplicate}
          data={dataModal.smartA1BatchDuplicate}
          handleClose={handleDuplicateClose}
          handleChooseOption={(
            type: string,
            has_batch: (string | number)[],
            no_batch: (string | number)[]
          ) => {
            const allOrderIds = ordersShortInfo.map((o) => o.id)
            const orderIds =
              type === A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTION.EXCLUDE
                ? allOrderIds.filter((id) => !has_batch.includes(id))
                : allOrderIds

            showModal('smart', {
              orderIds,
            })
            setHasShow((prev) => ({...prev, smartA1BatchDuplicate: false}))
          }}
        />
      )}
      {hasShow.fromAddressOrders && (
        <A1ShipFromOrderModal
          show={hasShow.fromAddressOrders}
          data={dataModal.fromAddressOrders}
          handleSubmit={handleShipFromSubmit}
          handleClose={handleShipFromClose}
        />
      )}
      {hasShow.confirmAction && (
        <ConfirmActionModal
          show={hasShow.confirmAction}
          title={intl.formatMessage({id: 'SHIPMENT_ADDRESS_VALIDATION'})}
          message={contentConfirmAction}
          messagePrimary={intl.formatMessage({id: 'SUBMIT'})}
          messageSecondary={intl.formatMessage({id: 'CANCEL'})}
          handlePrimaryAction={handleConfirmPrimaryAction}
          handleSecondaryAction={handleConfirmSecondaryAction}
          handleClose={handleConfirmClose}
        />
      )}
      <ModalTemplate
        id='gori_modal_add_to_A1_batch_modal'
        show={show && !hasShow.batchesDuplicate && !hasShow.smart}
        handleClose={() => handleClose()}
        disabledSecondaryBtn={loading.submit}
        disabledPrimaryBtn={loading.first || loading.submit}
        loadingPrimaryBtn={loading.submit}
        onClickPrimaryBtn={formik.handleSubmit}
      >
        {loading.first ? (
          Array.from({length: 3}).map((item, index) => (
            <div
              className='d-flex flex-column border rounded mb-6 p-6 border-secondary cursor-no-drop'
              key={index}
            >
              <div className='fs-2 col-4 mb-3 placeholder placeholder-lg rounded-2 bg-secondary' />
              <div className='fs-5 col-8 placeholder placeholder-lg rounded-2 bg-secondary' />
            </div>
          ))
        ) : (
          <div>
            <div
              onClick={() =>
                handleSelectOption(A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.SMART)
              }
              className={clsx('border rounded mb-6 p-6 ', {
                'cursor-not-allowed': disabledSmartA1Batch,
                'cursor-pointer': !disabledSmartA1Batch,
                'border-secondary': !isSelectSmartA1Batch,
                'opacity-50':
                  (formik.values.active_box && !isSelectSmartA1Batch) || disabledSmartA1Batch,
                'border-primary bg-light-primary': isSelectSmartA1Batch,
              })}
            >
              <div>
                <h3 className='fw-bolder fs-2 d-flex align-items-center mb-3'>
                  {intl.formatMessage({id: 'SMART_A1_BATCH'})}
                  <KTSVG
                    path='/media/gori/a1/verify.svg'
                    className={clsx('svg-icon svg-icon-1 ms-2', {
                      'text-primary': isSelectSmartA1Batch,
                      'text-gray-400': !isSelectSmartA1Batch,
                    })}
                  />
                </h3>
                <span>
                  {intl.formatMessage({id: 'GIVE_A_SUGGESTION_USING_GORI_AI_OPTIMIZATION'})}
                </span>
              </div>
            </div>
            <div
              onClick={() =>
                handleSelectOption(A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE)
              }
              className={clsx('border rounded mb-6 p-6 cursor-pointer', {
                'border-secondary': !isSelectCreateNewA1Batch,
                'opacity-50': formik.values.active_box && !isSelectCreateNewA1Batch,
                'border-primary bg-light-primary': isSelectCreateNewA1Batch,
              })}
            >
              <div>
                <h3 className='fw-bolder fs-2 mb-3'>
                  {intl.formatMessage({id: 'CREATE_NEW_A1_BATCH'})}
                </h3>
                <span className=''>
                  {intl.formatMessage({id: 'GIVE_A_UNIQUE_NAME_FOR_YOUR_A1_BATCH'})}
                </span>
              </div>
              {isSelectCreateNewA1Batch && (
                <div className='mt-6'>
                  <InputTextFormik
                    label={intl.formatMessage({id: 'A1_BATCH_NAME'})}
                    formik={formik}
                    name={A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.CREATE}
                    placeholder='100000'
                    disabled={loading.submit}
                    required
                  />
                </div>
              )}
            </div>
            <div
              onClick={() =>
                handleSelectOption(A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING)
              }
              className={clsx('border rounded mb-6 p-6 cursor-pointer', {
                'border-secondary': !isSelectAddToExistingA1Batch,
                'opacity-50': formik.values.active_box && !isSelectAddToExistingA1Batch,
                'border-primary bg-light-primary': isSelectAddToExistingA1Batch,
              })}
            >
              <div>
                <h3 className='fw-bolder fs-2 mb-3'>
                  {intl.formatMessage({id: 'ADD_TO_EXISTING_A1_BATCH'})}
                </h3>
                <span className=''>
                  {intl.formatMessage({id: 'SELECT_A_A1_BATCH_TO_ADD_YOUR_ORDER_TO'})}
                </span>
              </div>
              {isSelectAddToExistingA1Batch && (
                <div className='mt-6'>
                  <SelectFormik
                    label={intl.formatMessage({id: 'SELECT_A_A1_BATCH'})}
                    placeholder={intl.formatMessage({id: 'A1_BATCH'})}
                    formik={formik}
                    name={A1_CONSTANTS.STATUS_ADD_TO_A1_BATCH.ACTIVE_BOX.EXISTING}
                    options={optionsBatches}
                    disabled={loading.submit}
                    emptyDefault={false}
                    required
                  />
                </div>
              )}
            </div>
            {formik.errors.active_box && formik.touched.active_box && (
              <div className='mt-4 text-danger text-center'>{formik.errors.active_box}</div>
            )}
          </div>
        )}
      </ModalTemplate>
    </>
  )
}

export {AddToA1BatchModal}
